import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader } from "@vibe/core";
import useCollatedUpdates from "../../../hooks/api/useCollatedUpdates";
import ActivityEntry from "./ActivityEntry";
import ActivityEventEntry from "./ActivityEventEntry";
import { CollatedEntry } from "../../../types/collatedUpdates";

// Tabs collapsed to a single "All activity" view. Kept for easy re-enable.
// type Tab = "updates" | "all";

// Loading placeholder that mirrors a real entry's shape (30px avatar, a
// name + timestamp row, then a content line) so the transition to loaded
// content doesn't reflow. `contentWidth` varies per row for a natural feel.
const SkeletonRow = ({ contentWidth }: { contentWidth: string }) => (
  <div className="px-4 pt-4 pb-3.5 border-b border-t-0 border-x-0 border-solid border-[#F4F5F8]">
    <div className="flex gap-[11px] items-start animate-pulse">
      <div className="flex-none w-[30px] h-[30px] rounded-full bg-[#E6E9EF]" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="h-3 w-24 rounded bg-[#E6E9EF]" />
          <div className="h-2.5 w-12 rounded bg-[#EEF0F4]" />
        </div>
        <div className="mt-2.5 h-3 rounded bg-[#E6E9EF]" style={{ width: contentWidth }} />
      </div>
    </div>
  </div>
);

const ActivityLog = ({ itemId }: { itemId: string }) => {
  // Single "All activity" view: always fetch the full scope (updates +
  // activity events). Tab state kept commented out for easy re-enable.
  // const [tab, setTab] = useState<Tab>("updates");
  const scope: "updates" | "all" = "all";
  const { entries, hasMore, isLoadingInitial, isLoadingMore, loadMore } =
    useCollatedUpdates(itemId, scope);
  const [newestFirst, setNewestFirst] = useState(true);
  // Matches the mockup: the log starts collapsed as a pill, expands on click.
  const [collapsed, setCollapsed] = useState(true);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const sorted: CollatedEntry[] = useMemo(() => {
    const copy = [...entries];
    copy.sort((a, b) => {
      const diff = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      return newestFirst ? diff : -diff;
    });
    return copy;
  }, [entries, newestFirst]);

  // Infinite scroll: load the next page when the sentinel scrolls into view.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || isLoadingMore) return;
    if (typeof IntersectionObserver === "undefined") return; // jsdom / unsupported
    const observer = new IntersectionObserver((entriesObs) => {
      if (entriesObs[0]?.isIntersecting) loadMore();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, loadMore]);

  // <button> doesn't inherit font-family and ships with UA background/border,
  // which is why the buttons looked "default styled". `flatButton` is the base
  // reset; `buttonReset` adds `border-none` for buttons that must show no border
  // at all. Tabs use `flatButton` instead, because they DO need a bottom border
  // (adding `border-none` here would win the cascade and hide the underline).
  const flatButton = "bg-transparent cursor-pointer !font-figtree";
  const buttonReset = `${flatButton} border-none`;

  // Tab button styling kept for easy re-enable of the Updates/All activity tabs.
  // const tabClass = (active: boolean) =>
  //   `${flatButton} py-2.5 px-0.5 -mb-px inline-flex items-center gap-1.5 text-[13.5px] border-x-0 border-t-0 border-b-2 border-solid ${
  //     active
  //       ? "border-primary-color text-primary-color font-semibold"
  //       : "border-transparent text-[#676879] font-medium"
  //   }`;

  if (collapsed) {
    return (
      <div className="w-full flex items-center justify-between py-[14px] px-4 rounded-xl border border-solid border-[#E6E9EF]">
        <div className="flex items-center gap-3">
          <span className="flex-none flex items-center justify-center w-[34px] h-[34px] rounded-[9px] bg-[#F0F1F5] text-[#676879]">
            <svg width="17" height="17" viewBox="0 0 20 20" fill="none">
              <path
                d="M3 5.5A2 2 0 0 1 5 3.5h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H8l-4 3.2V13.5H5a2 2 0 0 1-2-2Z"
                stroke="currentColor"
                strokeWidth={1.5}
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold text-[#323338] leading-[1.2]">
              Activity log
            </div>
            <div className="mt-0.5 text-[12.5px] text-[#9699A6]">Every update across boards</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className={`${buttonReset} inline-flex items-center gap-1.5 p-0 text-[13px] font-semibold text-[#0073EA]`}
        >
          Expand<span className="text-[10px]">▾</span>
        </button>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-baseline gap-2 mx-0.5 mb-2.5">
        <h2 className="m-0 !font-poppins text-[15px] font-semibold text-[#323338]">Activity log</h2>
        <span className="text-[13px] text-[#9699A6]">Every update across boards</span>
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className={`${buttonReset} ml-auto inline-flex items-center gap-1.5 p-0 text-[12.5px] font-semibold text-[#0073EA]`}
        >
          Collapse<span className="text-[10px]">▴</span>
        </button>
      </div>

      <div className="rounded-xl border border-solid border-[#E6E9EF] bg-white">
        <div className="flex items-center gap-[22px] px-[18px] pt-2 border-b border-t-0 border-x-0 border-solid border-[#F2F3F6]">
          {/* Tabs collapsed to a single "All activity" view. Kept for re-enable.
          <button
            type="button"
            onClick={() => setTab("updates")}
            className={tabClass(tab === "updates")}
          >
            Updates
          </button>
          <button
            type="button"
            onClick={() => setTab("all")}
            className={tabClass(tab === "all")}
          >
            All activity
          </button>
          */}
          <button
            type="button"
            onClick={() => setNewestFirst((v) => !v)}
            title="Toggle order"
            aria-label="Toggle sort order"
            className={`${buttonReset} ml-auto flex items-center gap-1.5 py-2.5 p-0 text-[12.5px] font-semibold text-[#676879]`}
          >
            ⇅ {newestFirst ? "Newest" : "Oldest"}
          </button>
        </div>

        {isLoadingInitial ? (
          <div>
            <SkeletonRow contentWidth="72%" />
            <SkeletonRow contentWidth="54%" />
            <SkeletonRow contentWidth="63%" />
          </div>
        ) : sorted.length === 0 ? (
          <div className="px-4 py-10 text-center text-[13px] text-[#9699A6]">
            No updates yet across connected items
          </div>
        ) : (
          <div>
            {sorted.map((entry) =>
              entry.kind === "activity" ? (
                <ActivityEventEntry
                  key={entry.id}
                  entry={entry}
                  isFirstCreate={entry.isOriginalCreate ?? true}
                />
              ) : (
                <ActivityEntry key={entry.id} entry={entry} />
              ),
            )}
            {hasMore && (
              <div ref={sentinelRef} className="flex justify-center py-3">
                {isLoadingMore ? (
                  <Loader size={20} />
                ) : (
                  <button
                    type="button"
                    onClick={loadMore}
                    className={`${buttonReset} p-0 text-[12.5px] font-semibold text-[#0073EA]`}
                  >
                    Load more
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLog;
